import { FC } from 'react';
import {
  Modal,
  PhoneInput,
  SelectInput,
  TextField,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { useAdminCreateClientAdmin } from 'store/admin-client-admins';
import { RoleType, roleTypes } from 'store/permissions';

interface Values {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  roleType: RoleType;
}

const INITIAL_VALUES: Values = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  roleType: 'admin',
};

interface Props extends ModalProps {
  clientId: string;
}

export const CreateAdminModal: FC<Props> = ({ clientId, closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { mutate: createClient, isLoading: isCreating } =
    useAdminCreateClientAdmin();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createClient(
      { clientId, data: values },
      {
        onSuccess: () => {
          closeModal();
          predefinedToasts.success.added();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('client.add_client')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: !isDirty || isCreating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_first_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_last_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <PhoneInput
                label={t('client.admin_phone')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_email')}
                name={name}
                value={value}
                onChange={onChange}
                type="email"
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="roleType"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('permissions.role.title')}
                name={name}
                value={value}
                onChange={onChange}
                options={roleTypes
                  .filter((roleType) => roleType !== 'custom')
                  .map((roleType) => ({
                    label: t(`permissions.roleType.${roleType}`),
                    value: roleType,
                  }))}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
